import { useState } from 'react';
import { C, SERIF } from '../../theme';
import { Icon } from '../../icons';
import { Loading, Empty } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api, ApiError, API_BASE } from '../../api';
import type { RequestCategory, RequestRecord, RequestEventRecord, RequestStatus, TenancyRow } from '../../api';
import { useAsync } from '../../useAsync';
import { fmtDate } from '../../format';

const FLOW: RequestStatus[] = ['submitted', 'seen', 'assigned', 'in_progress', 'done'];

const STATUS_LABEL: Record<RequestStatus, string> = {
  submitted: 'Submitted',
  seen: 'Seen',
  assigned: 'Assigned',
  in_progress: 'In progress',
  done: 'Done',
};

const CATEGORY_LABEL: Record<RequestCategory, string> = {
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  ac: 'Air-conditioning',
  structure: 'Structural',
  other: 'Other',
};

/** A request-status chip (distinct from the money pills). */
function StatusChip({ status }: { status: RequestStatus }) {
  const tone: Record<RequestStatus, [string, string]> = {
    submitted: [C.redSoft, C.red],
    seen: [C.amberSoft, '#7a5518'],
    assigned: [C.amberSoft, '#7a5518'],
    in_progress: [C.soft, C.accent2],
    done: [C.greenSoft, C.green],
  };
  const [bg, fg] = tone[status];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: bg, color: fg, whiteSpace: 'nowrap' }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: 'currentColor' }} />
      {STATUS_LABEL[status]}
    </span>
  );
}

type RequestDetail = RequestRecord & { events: RequestEventRecord[] };

async function fetchDetail(id: string): Promise<RequestDetail> {
  const res = await fetch(`${API_BASE}/requests/${id}`, { credentials: 'include' });
  const body = (await res.json()) as { ok: boolean; data?: RequestDetail; error?: { message?: string } };
  if (!body.ok || !body.data) throw new ApiError(res.status, body.error?.message ?? 'Could not load request');
  return body.data;
}

interface MaintData {
  requests: RequestRecord[];
  byTenancy: Record<string, TenancyRow>;
}

/**
 * Maintenance queue — every request tenants raised, with category, live status
 * and which tenant it came from. Open one to walk the event timeline, advance
 * the status, or add a note the tenant will see on their side.
 */
type MaintFilter = 'all' | 'action' | 'progress' | 'done';
const needsAction = (s: RequestStatus) => s === 'submitted';
const inProgress = (s: RequestStatus) => s === 'seen' || s === 'assigned' || s === 'in_progress';

export function MaintenancePage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const [filter, setFilter] = useState<MaintFilter>('all');
  const [showLog, setShowLog] = useState(false);
  const { data, loading, error, reload } = useAsync<MaintData>(async () => {
    const [requests, tenancies] = await Promise.all([
      api.ownerRequests(),
      api.tenancies().catch(() => [] as TenancyRow[]),
    ]);
    const byTenancy: Record<string, TenancyRow> = {};
    for (const t of tenancies) byTenancy[t.id] = t;
    return { requests, byTenancy };
  }, []);

  if (openId) {
    return <RequestDetailView id={openId} tenant={data?.byTenancy[data.requests.find((r) => r.id === openId)?.tenancyId ?? '']} onBack={() => { setOpenId(null); reload(); }} />;
  }

  const all = [...(data?.requests ?? [])].sort((a, b) => {
    const ao = a.status === 'done' ? 1 : 0;
    const bo = b.status === 'done' ? 1 : 0;
    if (ao !== bo) return ao - bo;
    return (b.createdAt ?? 0) - (a.createdAt ?? 0);
  });
  const actionCount = all.filter((r) => needsAction(r.status)).length;
  const counts: Record<MaintFilter, number> = {
    all: all.length,
    action: actionCount,
    progress: all.filter((r) => inProgress(r.status)).length,
    done: all.filter((r) => r.status === 'done').length,
  };
  const requests = all.filter((r) =>
    filter === 'all' ? true : filter === 'action' ? needsAction(r.status) : filter === 'progress' ? inProgress(r.status) : r.status === 'done',
  );

  const FILTERS: Array<[MaintFilter, string]> = [['all', 'All'], ['action', 'Needs action'], ['progress', 'In progress'], ['done', 'Done']];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: SERIF, fontSize: 22, margin: '0 0 3px', fontWeight: 600 }}>Maintenance</h2>
          <div style={{ color: C.muted, fontSize: 13.5 }}>
            {data ? (actionCount > 0 ? `${actionCount} need${actionCount === 1 ? 's' : ''} action · ${all.length} total` : `All caught up · ${all.length} total`) : 'Requests from your tenants'}
          </div>
        </div>
        <button onClick={() => setShowLog((s) => !s)} style={primaryBtn}>
          <Icon n={showLog ? 'chev' : 'wrench'} size={15} color="#fff" style={showLog ? { transform: 'rotate(-90deg)' } : undefined} />
          {showLog ? 'Close' : 'Log issue'}
        </button>
      </div>

      {showLog && (
        <LogPanel tenancies={Object.values(data?.byTenancy ?? {})} onDone={() => { setShowLog(false); reload(); }} />
      )}

      {data && all.length > 0 && (
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          {FILTERS.map(([key, label]) => {
            const on = filter === key;
            return (
              <button key={key} onClick={() => setFilter(key)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, border: `1px solid ${on ? C.accent : C.line}`, background: on ? C.accent : C.card, color: on ? '#fff' : C.ink2, fontWeight: 600, fontSize: 12.6, cursor: 'pointer' }}>
                {label}<span style={{ fontSize: 11, opacity: 0.8 }}>{counts[key]}</span>
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <Loading label="Loading requests…" minHeight={360} />
      ) : error ? (
        <Empty icon="wrench" title="Maintenance unavailable" hint="We couldn't reach the maintenance queue." minHeight={360}>
          <button onClick={reload} style={retryBtn}>Retry</button>
        </Empty>
      ) : all.length === 0 ? (
        <Empty icon="wrench" title="Nothing to fix" hint="When a tenant raises a request, or you log one, it lands here to track." minHeight={320}>
          <button onClick={() => setShowLog(true)} style={retryBtn}>Log an issue</button>
        </Empty>
      ) : requests.length === 0 ? (
        <Empty icon="wrench" title={`Nothing ${filter === 'action' ? 'needs action' : filter === 'progress' ? 'in progress' : 'here'}`} hint="Try another filter." minHeight={220} />
      ) : (
        <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, overflow: 'hidden' }}>
          {requests.map((r, i) => {
            const t = data?.byTenancy[r.tenancyId];
            return (
              <div key={r.id} onClick={() => setOpenId(r.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', borderTop: i ? `1px solid ${C.line2}` : 0, cursor: 'pointer' }}>
                <span style={{ width: 38, height: 38, borderRadius: 10, background: C.soft, color: C.accent2, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                  <Icon n="wrench" size={17} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                    <b style={{ color: C.ink, fontWeight: 600, fontSize: 14 }}>{CATEGORY_LABEL[r.category]}{t ? ` · ${t.tenantName}` : ''}</b>
                    <OriginBadge role={r.createdByRole} />
                  </div>
                  <small style={{ color: C.muted, fontSize: 12, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.description || (t ? `${t.unitLabel}` : '–')}{r.createdAt ? ` · ${fmtDate(r.createdAt)}` : ''}
                  </small>
                </div>
                <StatusChip status={r.status} />
                <Icon n="chev" size={15} color={C.muted} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** "By tenant" / "By owner" origin stamp. */
function OriginBadge({ role }: { role?: string | null }) {
  if (role !== 'tenant' && role !== 'owner') return null;
  const tenant = role === 'tenant';
  return (
    <span style={{ fontSize: 10.5, fontWeight: 600, color: tenant ? C.accent2 : C.muted, background: tenant ? C.soft : C.line2, borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>
      {tenant ? 'By tenant' : 'By owner'}
    </span>
  );
}

/** Owner logs a maintenance issue against one of their tenancies. */
function LogPanel({ tenancies, onDone }: { tenancies: TenancyRow[]; onDone: () => void }) {
  const notify = useToast();
  const [tenancyId, setTenancyId] = useState('');
  const [category, setCategory] = useState<RequestCategory>('plumbing');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const canSubmit = !!tenancyId && !!description.trim() && !busy;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.logRequest({ tenancyId, category, description: description.trim() });
      notify('Issue logged');
      onDone();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not log the issue');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label style={fieldLabel}>Tenancy
          <select value={tenancyId} onChange={(e) => setTenancyId(e.target.value)} style={input}>
            <option value="">Choose a tenancy…</option>
            {tenancies.map((t) => (<option key={t.id} value={t.id}>{t.unitLabel} · {t.tenantName}</option>))}
          </select>
        </label>
        <label style={fieldLabel}>Category
          <select value={category} onChange={(e) => setCategory(e.target.value as RequestCategory)} style={input}>
            {(['plumbing', 'electrical', 'ac', 'structure', 'other'] as RequestCategory[]).map((k) => (<option key={k} value={k}>{CATEGORY_LABEL[k]}</option>))}
          </select>
        </label>
      </div>
      <label style={fieldLabel}>What's the issue?
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="e.g. Water seepage on the north wall, reported by the tenant on call" style={{ ...input, resize: 'vertical' }} />
      </label>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={submit} disabled={!canSubmit} style={{ ...primaryBtn, opacity: canSubmit ? 1 : 0.55, cursor: canSubmit ? 'pointer' : 'default' }}>
          {busy ? 'Logging…' : 'Log issue'}
        </button>
      </div>
    </div>
  );
}

function RequestDetailView({ id, tenant, onBack }: { id: string; tenant?: TenancyRow; onBack: () => void }) {
  const notify = useToast();
  const [note, setNote] = useState('');
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data, loading, error, reload } = useAsync<RequestDetail>(() => fetchDetail(id), [id]);

  const nextStatuses = data ? FLOW.slice(FLOW.indexOf(data.status) + 1) : [];

  async function advance(status: RequestStatus) {
    setBusy(true);
    try {
      await api.advanceRequest(id, { status, note: note.trim() || undefined });
      notify(`Marked ${STATUS_LABEL[status].toLowerCase()}`);
      setNote('');
      reload();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not update status');
    } finally {
      setBusy(false);
    }
  }

  async function addNote() {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.addRequestEvent(id, { note: note.trim(), internal });
      notify(internal ? 'Internal note added' : 'Note added. The tenant can see it.');
      setNote('');
      reload();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not add note');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div onClick={onBack} style={{ fontSize: 12.5, color: C.accent, cursor: 'pointer', fontWeight: 600 }}>← Maintenance</div>

      {loading ? (
        <Loading label="Loading request…" minHeight={340} />
      ) : error || !data ? (
        <Empty icon="wrench" title="Request unavailable" hint="We couldn't load this request." minHeight={340}>
          <button onClick={reload} style={retryBtn}>Retry</button>
        </Empty>
      ) : (
        <>
          <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <h3 style={{ margin: '0 0 3px', fontSize: 16, fontWeight: 650, color: C.ink }}>{CATEGORY_LABEL[data.category]}</h3>
                  <OriginBadge role={data.createdByRole} />
                </div>
                <div style={{ color: C.muted, fontSize: 12.8 }}>{tenant ? `${tenant.unitLabel} · ${tenant.tenantName}` : 'Tenant request'}{data.createdAt ? ` · raised ${fmtDate(data.createdAt)}` : ''}</div>
              </div>
              <StatusChip status={data.status} />
            </div>
            {data.description && <p style={{ margin: '10px 0 0', fontSize: 13.5, color: C.ink2, lineHeight: 1.5 }}>{data.description}</p>}
          </div>

          {/* timeline */}
          <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, padding: '14px 16px' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 650, color: C.ink, textTransform: 'uppercase', letterSpacing: '.04em' }}>Timeline</h4>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {data.events.length === 0 ? (
                <div style={{ color: C.muted, fontSize: 13 }}>No activity yet.</div>
              ) : (
                data.events.map((e, i) => {
                  const last = i === data.events.length - 1;
                  return (
                    <div key={e.id} style={{ display: 'flex', gap: 12 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 12 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 999, background: last ? C.accent : C.line, border: `2px solid ${C.card}`, boxShadow: `0 0 0 1.5px ${last ? C.accent : C.line}`, flex: 'none', marginTop: 3 }} />
                        {!last && <span style={{ width: 2, flex: 1, background: C.line2, minHeight: 14 }} />}
                      </div>
                      <div style={{ paddingBottom: last ? 0 : 14, flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <b style={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>{STATUS_LABEL[e.status]}</b>
                          {e.actorRole && <span style={{ fontSize: 10.5, fontWeight: 600, color: C.muted }}>{e.actorRole === 'tenant' ? 'tenant' : 'you'}</span>}
                          {e.internal && <span style={{ fontSize: 10, fontWeight: 700, color: '#7a5518', background: C.amberSoft, borderRadius: 999, padding: '2px 7px', textTransform: 'uppercase', letterSpacing: '.04em' }}>Internal</span>}
                          {e.createdAt && <small style={{ color: C.muted, fontSize: 11.5 }}>{fmtDate(e.createdAt)}</small>}
                        </div>
                        {e.note && <div style={{ fontSize: 13, color: C.ink2, marginTop: 2 }}>{e.note}</div>}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* actions */}
          <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 11 }}>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note (optional, attached to the next status change too)…"
              rows={2}
              style={{ border: `1px solid ${C.line}`, borderRadius: 9, padding: '9px 11px', fontSize: 13.5, color: C.ink, background: '#fff', fontFamily: 'inherit', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={addNote} disabled={busy || !note.trim()} style={{ ...ghostBtn, opacity: busy || !note.trim() ? 0.55 : 1 }}>Add note</button>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: internal ? C.ink2 : C.muted, cursor: 'pointer' }}>
                <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
                Internal only
              </label>
              <div style={{ flex: 1 }} />
              {nextStatuses.length === 0 ? (
                <span style={{ fontSize: 12.8, color: C.hi, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon n="check" size={15} /> Closed</span>
              ) : (
                nextStatuses.map((s) => (
                  <button key={s} onClick={() => advance(s)} disabled={busy} style={{ ...(s === nextStatuses[0] ? primaryBtn : ghostBtn), opacity: busy ? 0.6 : 1 }}>
                    Mark {STATUS_LABEL[s].toLowerCase()}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  background: C.accent,
  color: '#fff',
  border: 0,
  borderRadius: 9,
  padding: '9px 14px',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
};

const ghostBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  background: C.card,
  color: C.accent,
  border: `1px solid ${C.line}`,
  borderRadius: 9,
  padding: '9px 14px',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
};

const retryBtn: React.CSSProperties = {
  marginTop: 4,
  border: `1px solid ${C.line}`,
  background: C.card,
  color: C.accent,
  fontWeight: 600,
  fontSize: 13,
  padding: '8px 16px',
  borderRadius: 9,
  cursor: 'pointer',
};

const fieldLabel: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  fontSize: 12,
  fontWeight: 600,
  color: C.muted,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
};

const input: React.CSSProperties = {
  border: `1px solid ${C.line}`,
  borderRadius: 9,
  padding: '9px 11px',
  fontSize: 13.5,
  color: C.ink,
  background: '#fff',
  fontFamily: 'inherit',
  textTransform: 'none',
  letterSpacing: 'normal',
  fontWeight: 400,
};
